/**
 * The six error shapes of design.md's error taxonomy, and the validation
 * `Finding` they carry.
 *
 * Every shape carries a stable machine-readable `code` and the request
 * `correlationId` (R17.8), and nothing else reaches the client. Three properties
 * of the taxonomy are schema decisions rather than handler discipline, which is
 * the point of stating them here:
 *
 * - `forbidden` carries no identifier, no count and no field value (R2.8), so a
 *   denial cannot confirm that the requested record exists.
 * - `not_found` carries nothing beyond the code and the correlation id, so the
 *   unknown, the unreadable and the soft-deleted identifier are byte-identical
 *   (R3.6, R12.11, R17.11). One fixed body, one helper, no variant field to leak
 *   through.
 * - No shape has a slot for Initiative content, with one required exception: the
 *   version conflict of R5.8 must report the stored value per conflicting field so
 *   the user can resubmit without losing work. That payload only ever reaches a
 *   user who already holds `initiative:write` on a record they just read.
 *
 * Messages are absent by design. A failure is reported by `code`, and a finding
 * by rule identifier, tier, field path and message *key*; the wording lives in
 * the i18next resources (R18.9). `Finding.message` is present only to carry the
 * administrator-configured override text that R8.9 permits for a configurable
 * rule.
 */
import { z } from 'zod';
import { initiativeStatusSchema, validationTierSchema } from './enums.js';
import { VALIDATION_MESSAGE_MAX_LENGTH } from './limits.js';
import { fieldIssueSchema, fieldPathSchema, ruleIdSchema, targetPathSchema, versionSchema } from './primitives.js';
/**
 * One validation finding (design.md, "Validation engine").
 *
 * `tier` is the *effective* tier after configuration (R8.9), which is why a
 * finding carries it rather than the client deriving it from the rule identifier.
 * `detail` carries the values a message interpolates - the entered split total and
 * the affected Platforms of R4.5, the uncovered ranges of R6.5 - as data, so no
 * sentence is assembled on the server.
 */
export const findingSchema = z.object({
    ruleId: ruleIdSchema,
    tier: validationTierSchema,
    /** i18next key of the message; the words live in the resource files (R18.9). */
    messageKey: z.string().min(1),
    /** Administrator-configured override text, 1 to 500 characters, or `null` (R8.9). */
    message: z.string().min(1).max(VALIDATION_MESSAGE_MAX_LENGTH).nullable().default(null),
    /** The affected fields, reported to the user with the finding (R8.2, R8.4). */
    fields: z.array(fieldPathSchema).default([]),
    detail: z.record(z.unknown()).nullable().default(null),
});
/** The six codes of the error taxonomy, in the order the design's table lists them. */
export const ERROR_CODES = Object.freeze([
    'unauthenticated',
    'forbidden',
    'not_found',
    'validation_failed',
    'conflict',
    'internal',
]);
/** See {@link ERROR_CODES}. */
export const errorCodeSchema = z.enum(ERROR_CODES);
/** The HTTP status each shape is returned with. */
export const HTTP_STATUS_BY_ERROR_CODE = Object.freeze({
    unauthenticated: 401,
    forbidden: 403,
    not_found: 404,
    validation_failed: 422,
    conflict: 409,
    internal: 500,
});
/** The correlation identifier every response carries, one per request (R17.8). */
const correlationIdSchema = z.string().uuid();
/**
 * 401. No session, or a session that reached the 8-hour absolute or 60-minute
 * idle cap (R1.1, R1.5, R1.6).
 */
export const unauthenticatedErrorSchema = z.object({
    code: z.literal('unauthenticated'),
    correlationId: correlationIdSchema,
    /** Where to start the OIDC exchange. */
    authorizeUrl: z.string().min(1),
    /** The path to return to afterwards, retained server-side and echoed for the client (R1.1). */
    targetPath: targetPathSchema.nullable().default(null),
});
/**
 * 403. A capability the principal does not hold (R2.5, R2.7, R9.3).
 *
 * Deliberately empty beyond the code and the correlation id: R2.8 forbids the
 * denied record's field values *and* the count of denied records.
 */
export const forbiddenErrorSchema = z.object({
    code: z.literal('forbidden'),
    correlationId: correlationIdSchema,
});
/**
 * 404. An unknown, unreadable or soft-deleted identifier - one fixed body for all
 * three (R3.6, R12.11, R17.11).
 */
export const notFoundErrorSchema = z.object({
    code: z.literal('not_found'),
    correlationId: correlationIdSchema,
});
/**
 * 422. Validation blocked the action (R8.2, R8.4, R8.5).
 *
 * Two authorities can block, and both report by field path: `findings` carries
 * the Validation_Engine's complete finding set - every tier, never a prefix
 * (R8.12) - and `fieldIssues` carries the request-shape rejections this package's
 * schemas raise. One shape, so the client renders one validation summary (R5.7).
 */
export const validationFailedErrorSchema = z.object({
    code: z.literal('validation_failed'),
    correlationId: correlationIdSchema,
    findings: z.array(findingSchema).default([]),
    fieldIssues: z.array(fieldIssueSchema).default([]),
});
/** One field of a version-conflict diff: what is stored against what was submitted (R5.8). */
export const fieldConflictSchema = z.object({
    path: fieldPathSchema,
    storedValue: z.unknown(),
    submittedValue: z.unknown(),
});
/**
 * The three conflict sources, distinguished so the client can render the right
 * recovery: resubmit a diff, pick a permitted transition, or re-run a preview.
 */
export const conflictDetailSchema = z.discriminatedUnion('kind', [
    /** R5.8: the submitted `version` is not the stored one. No automatic retry. */
    z.object({
        kind: z.literal('version'),
        storedVersion: versionSchema,
        submittedVersion: versionSchema,
        fields: z.array(fieldConflictSchema).default([]),
    }),
    /** R9.13: the requested transition is absent from the permitted set. */
    z.object({
        kind: z.literal('transition'),
        currentStatus: initiativeStatusSchema,
        permittedTargetStatuses: z.array(initiativeStatusSchema),
    }),
    /**
     * R6.7-R6.9 (design.md, "Concurrency"): a Sales_Cycle apply ran at
     * `REPEATABLE READ` and hit a serialization failure, so the impact list the
     * user confirmed may no longer describe the current state. Nothing persisted;
     * the client must re-preview before applying again. No automatic retry, for
     * the same reason a version mismatch has none - a silent retry could reassign
     * against a list the user never saw.
     */
    z.object({
        kind: z.literal('stale_preview'),
    }),
]);
/**
 * 409. A version mismatch (R5.8), a transition that is not permitted (R9.13) or
 * a stale Sales_Cycle preview after a serialization failure (R6.7-R6.9).
 */
export const conflictErrorSchema = z.object({
    code: z.literal('conflict'),
    correlationId: correlationIdSchema,
    conflict: conflictDetailSchema,
});
/**
 * 500. Anything unexpected, including a deferred constraint trigger firing at
 * commit - which means the Validation_Engine and the database disagree and is a
 * defect, not a user error (design.md, "Validation failures versus faults").
 */
export const internalErrorSchema = z.object({
    code: z.literal('internal'),
    correlationId: correlationIdSchema,
});
/** Every error response, discriminated by `code`. */
export const apiErrorSchema = z.discriminatedUnion('code', [
    unauthenticatedErrorSchema,
    forbiddenErrorSchema,
    notFoundErrorSchema,
    validationFailedErrorSchema,
    conflictErrorSchema,
    internalErrorSchema,
]);
//# sourceMappingURL=errors.js.map